import { notNull } from "../helpers";
import type { FFId, FFInput, FFInputKey } from "./inputs";
import { z } from "zod";

export type FFShape = {
  id: (typeof FFId)[keyof typeof FFId];
} & {
  [K in FFInputKey]?: (typeof FFInput)[K];
} & z.ZodRawShape;

// Generic inference accepts extra keys, so they are mapped to `never` here.
type NoExtraKeys<S> = S & Record<Exclude<keyof S, "id" | FFInputKey>, never>;

// Input is unknown so schemas with defaults or transforms are accepted.
type PayloadSchema<P> = z.ZodType<P, z.ZodTypeDef, unknown>;

export const targetingListsSchema = z.object({
  enabled: z.boolean().default(false),
  studies: z.array(z.string()).optional(),
  users: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  excludedStudies: z.array(z.string()).optional(),
  excludedOrgs: z.array(z.string()).optional(),
});

export abstract class FlagDefinition<S extends FFShape, P> {
  readonly schema: z.ZodObject<S>;

  constructor(
    readonly key: string,
    readonly inputs: NoExtraKeys<S>,
    /** Without one, `getPayload` never returns a payload. */
    readonly payloadSchema: PayloadSchema<P> | undefined
  ) {
    this.schema = z.object<S>(inputs);
  }
}

const isObjectSchema = (
  schema: z.ZodTypeAny
): schema is z.ZodObject<z.ZodRawShape> => schema instanceof z.ZodObject;

// An excludedOrgs entry can only exclude an org the call site passes, so
// usesTargetingLists requires the flag to declare orgId.
type ListsOption<S> = S extends { orgId: (typeof FFInput)["orgId"] }
  ? { usesTargetingLists?: boolean }
  : { usesTargetingLists?: false };

type SuperAdminOption<S> = S extends {
  isSuperAdmin: (typeof FFInput)["isSuperAdmin"];
}
  ? { isAlwaysOnForSuperAdmins?: boolean }
  : { isAlwaysOnForSuperAdmins?: false };

export class FF<S extends FFShape, P = undefined> extends FlagDefinition<S, P> {
  readonly kind = "boolean";
  // Applied after PostHog matches; order is documented in docs/feature_flags.md.
  readonly usesTargetingLists: boolean;
  // Checked before PostHog, so super-admins skip the targeting lists too.
  readonly isAlwaysOnForSuperAdmins: boolean;
  /**
   * The lists plus the flag's own payload keys. Any other key fails the flag,
   * so a misspelled key can't silently drop an exclusion.
   */
  readonly allowedPayloadKeys: ReadonlySet<string>;

  constructor(
    key: string,
    inputs: NoExtraKeys<S>,
    options?: ListsOption<S> &
      SuperAdminOption<S> & {
        usesTargetingLists?: boolean;
        isAlwaysOnForSuperAdmins?: boolean;
        payload?: PayloadSchema<P>;
      }
  ) {
    super(key, inputs, options?.payload);
    this.usesTargetingLists = options?.usesTargetingLists ?? false;
    this.isAlwaysOnForSuperAdmins = options?.isAlwaysOnForSuperAdmins ?? false;
    const ownPayload = options?.payload;
    if (
      this.usesTargetingLists &&
      notNull(ownPayload) &&
      !isObjectSchema(ownPayload)
    ) {
      throw new Error(
        `${key}: a usesTargetingLists flag's payload schema must be a z.object, so its keys can sit beside the lists`
      );
    }
    this.allowedPayloadKeys = new Set([
      ...Object.keys(targetingListsSchema.shape),
      ...(notNull(ownPayload) && isObjectSchema(ownPayload)
        ? Object.keys(ownPayload.shape)
        : []),
    ]);
  }
}

export class VariantFF<S extends FFShape, P = undefined> extends FlagDefinition<
  S,
  P
> {
  readonly kind = "variant";

  constructor(
    key: string,
    inputs: NoExtraKeys<S>,
    options: { payload?: PayloadSchema<P> } = {}
  ) {
    super(key, inputs, options.payload);
  }
}
